<script setup>
import { computed, onMounted, ref, reactive, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
// eslint-disable-next-line no-restricted-imports
import { VTextarea } from 'vuetify/components'
import { useModalStore } from '@/stores/ModalStore'
import { useStorageProductsListStore } from '@/stores/storage/ProductsListStore'
import { useSuppliersStore } from '@/stores/storage/SuppliersStore'
import { normalizeInputValue, normalizeNumericValue, roundCurrency } from '@/utils/numbers'
import { StorageSerializers, useStorage } from '@vueuse/core'
import { showSuccessToast, showErrorToast, showWarningToast } from '@/utils/toast'
import emitter from '@/utils/emitter'
import productPlaceholder from "@images/placeholder-light.svg"
import { createValidationRules } from "@/utils/validationRules"
import { useStorageTransactionsStore } from "@/stores/storage/TransactionsStore"

const { t } = useI18n()
const validationRules = createValidationRules(t)
const { mobile, smAndUp, width } = useDisplay()
const modalStore = useModalStore()
const store = useStorageProductsListStore()
const suppliersStore = useSuppliersStore()
const transactionStore = useStorageTransactionsStore()

const THIS_MODAL_NAME = 'editStorageProduct'

const CLIPBOARD_KEY = 'storage-product-clipboard'
const CLIPBOARD_TTL_MS = 3 * 60 * 60 * 1000

const productId = modalStore.modalParams.editStorageProduct?.productId
const product = ref(null)
const editFormRef = ref(null)
const categorySearch = ref('')
const producerSearch = ref('')
const productSalonerSuppliers = ref([])
const supplierSearchQuery = ref('')
const lastSupplierSearchQuery = ref('')

const isLoading = ref(true)

const editForm = ref({
  id: null,
  name: '',
  description: '',
  notes: '',
  ean: '',
  producer: null,
  category: null,
  package: null,
  unit: '',
  tax: 23,
  price: 0,
  priceNet: 0,
  buyPrice: 0,
  buyPriceNet: 0,
  minimal: 0,
  type: 3,
  internalCode: '',
  imageUrl: '',
  salonerSuppliers: [],
  commission: null,
})

onMounted(async () => {
  if (!suppliersStore.suppliersData.isLoaded) {
    await suppliersStore.fetchSuppliers()
  }
  if (store.availableFilters.producers.length === 0 || store.availableFilters.categories.length === 0) {
    await store.fetchFilters()
  }

  if (productId) {
    const data = await store.fetchProduct(productId)
    if (data) {
      product.value = data
      editForm.value = {
        id: data.id,
        name: data.name ?? '',
        description: data.description ?? '',
        notes: data.notes ?? '',
        ean: data.ean ?? '',
        producer: data.producer ? data.producer : null,
        category: data.category ? data.category : null,
        package: data.package,
        unit: data.unit ?? '',
        tax: data.tax,
        price: data.price,
        priceNet: data.priceNet,
        buyPrice: data.buyPrice,
        buyPriceNet: data.buyPriceNet,
        minimal: data.minimal,
        type: normalizeNumericValue(data.type) ?? 3,
        internalCode: data.internalCode ?? '',
        imageUrl: data.imageUrl ?? '',
        salonerSuppliers: (data.salonerSuppliers || []).map(s => ({
          id: s.id,
          name: s.name,
        })),
        commission: data.commission ?? null,
      }
      productSalonerSuppliers.value = data.salonerSuppliers || [] // B: Don't remove - we add extra suppliers in case they no longer exist
    } else {
      modalStore.closeModal(THIS_MODAL_NAME)
    }
  } else {
    store.isLoadingProduct = false
    store.isSavingProduct = false
    editForm.value.name = modalStore.modalParams.editStorageProduct?.initialName || ''
    editForm.value.package = 1
    editForm.value.unit = transactionStore.DEFAULT_UNIT
    editForm.value.salonerSuppliers = suppliersStore.suppliersData.suppliers.find(s => s.id === Number(store.selectedSupplier) && s.canBeUnassigned) ? [suppliersStore.suppliersData.suppliers.find(s => s.id === Number(store.selectedSupplier) && s.canBeUnassigned)] : []
    editForm.value.producer = normalizeInputValue(store.selectedProducer) || null
    editForm.value.category = normalizeInputValue(store.selectedCategory) || null
    editForm.value.type = normalizeNumericValue(store.selectedType) ?? 3
    editForm.value.tax = normalizeNumericValue(store.selectedTax) ?? 23
    product.value = editForm.value
  }

  isLoading.value = false

})

const typeOptions = [
  { title: t('storage.import.sale'), value: 1 },
  { title: t('storage.import.usage'), value: 2 },
  { title: t('storage.import.both'), value: 3 },
]

const unitOptions = [
  { title: 'szt', value: 'szt' },
  { title: 'g', value: 'g' },
  { title: 'ml', value: 'ml' },
]

const taxOptions = [
  { title: '23%', value: 23 },
  { title: '8%', value: 8 },
  { title: '5%', value: 5 },
  { title: '0%', value: 0 },
  { title: 'zw', value: -1 },
]

const getVatMultiplier = (tax) => {
  const normalizedTax = normalizeNumericValue(tax)

  if (normalizedTax === null || normalizedTax < 0) return 1

  return 1 + (normalizedTax / 100)
}

const calculateGrossFromNet = (netValue, tax) => {
  const normalizedNetValue = normalizeNumericValue(netValue)

  if (normalizedNetValue === null) return null

  return roundCurrency(normalizedNetValue * getVatMultiplier(tax))
}

const calculateNetFromGross = (grossValue, tax) => {
  const normalizedGrossValue = normalizeNumericValue(grossValue)

  if (normalizedGrossValue === null) return null

  return roundCurrency(normalizedGrossValue / getVatMultiplier(tax))
}

const syncSellPriceFromNet = () => {
  editForm.value.price = calculateGrossFromNet(editForm.value.priceNet, editForm.value.tax)
}

const syncSellPriceFromGross = () => {
  editForm.value.priceNet = calculateNetFromGross(editForm.value.price, editForm.value.tax)
}

const syncBuyPriceFromNet = () => {
  editForm.value.buyPrice = calculateGrossFromNet(editForm.value.buyPriceNet, editForm.value.tax)
}

const syncBuyPriceFromGross = () => {
  editForm.value.buyPriceNet = calculateNetFromGross(editForm.value.buyPrice, editForm.value.tax)
}

const handleSellPriceNetChange = (value) => {
  editForm.value.priceNet = normalizeNumericValue(value)
  syncSellPriceFromNet()
}

const handleSellPriceGrossChange = (value) => {
  editForm.value.price = normalizeNumericValue(value)
  syncSellPriceFromGross()
}

const handleBuyPriceNetChange = (value) => {
  editForm.value.buyPriceNet = normalizeNumericValue(value)
  syncBuyPriceFromNet()
}

const handleBuyPriceGrossChange = (value) => {
  editForm.value.buyPrice = normalizeNumericValue(value)
  syncBuyPriceFromGross()
}

const handleSellTaxChange = (value) => {
  editForm.value.tax = value
  syncSellPriceFromNet()
  syncBuyPriceFromNet()
}

const isCommissionEnabled = computed(() => editForm.value.commission !== null)

const toggleCommission = (enabled) => {
  editForm.value.commission = enabled ? 0 : null
}

const createAutocompleteOptions = ({ availableItems, searchValue, modelValue }) => computed(() => {
  const options = []
  const seen = new Set()

  const addOption = value => {
    const normalized = normalizeInputValue(value)

    if (!normalized) return

    const key = normalized.toLocaleLowerCase()

    if (seen.has(key)) return

    seen.add(key)
    options.push({
      title: normalized,
      value: normalized,
    })
  }

  const normalizedSearch = normalizeInputValue(searchValue.value)

  if (
    normalizedSearch
    && !availableItems.value.some(name => name.toLocaleLowerCase() === normalizedSearch.toLocaleLowerCase())
  ) {
    addOption(searchValue.value)
  }

  availableItems.value.forEach(addOption)
  addOption(modelValue.value)

  return options
})

const categoryNames = computed(() =>
  store.availableFilters.categories
    .filter(c => c.id !== "Nie uzupełniono")
    .map(category => normalizeInputValue(category?.name))
    .filter(Boolean),
)

const shouldOfferCustomCategory = computed(() => {
  const searchValue = normalizeInputValue(categorySearch.value)

  if (!searchValue) return false

  return !categoryNames.value.some(name => name.toLocaleLowerCase() === searchValue.toLocaleLowerCase())
})

const categoryOptions = createAutocompleteOptions({
  availableItems: categoryNames,
  searchValue: categorySearch,
  modelValue: computed(() => editForm.value.category),
})

const commitCategorySearch = () => {
  const normalized = normalizeInputValue(categorySearch.value)

  if (normalized.length > 0) {
    editForm.value.category = normalized
    categorySearch.value = normalized
  }

}

const producerNames = computed(() =>
  store.availableFilters.producers
    .filter(p => p.id !== "Nie uzupełniono")
    .map(producer => normalizeInputValue(producer?.name))
    .filter(Boolean),
)

const shouldOfferCustomProducer = computed(() => {
  const searchValue = normalizeInputValue(producerSearch.value)

  if (!searchValue) return false

  return !producerNames.value.some(name => name.toLocaleLowerCase() === searchValue.toLocaleLowerCase())
})

const producerOptions = createAutocompleteOptions({
  availableItems: producerNames,
  searchValue: producerSearch,
  modelValue: computed(() => editForm.value.producer),
})

const commitProducerSearch = () => {
  const normalized = normalizeInputValue(producerSearch.value)

  if (normalized.length > 0) {
    editForm.value.producer = normalized
    producerSearch.value = normalized
  }
}

const supplierItems = computed(() => {
  const items = suppliersStore.suppliersData.suppliers.filter(s => s.canBeUnassigned).map(s => ({ id: s.id, name: s.name, canBeUnassigned: true }))
  const existingIds = new Set(items.map(i => i.id))

  for (const supplier of productSalonerSuppliers.value) {
    if (!existingIds.has(supplier.id)) {
      items.push({
        id: supplier.id,
        name: supplier.name,
        canBeUnassigned: supplier.canBeUnassigned,
      })
    }
  }

  const query = supplierSearchQuery.value?.trim().toLowerCase()
  const filtered = query
    ? items.filter(s => s.name?.toLowerCase().includes(query))
    : items

  if (filtered.length === 0) {
    filtered.push({
      id: '__add_new__',
      name: t('storage.transactions.not_found_add_new_supplier'),
      canBeUnassigned: true,
    })
  }

  return filtered
})

const handleSupplierAdd = async () => {
  const result = await modalStore.openModal('addSupplier', '', false, { query: lastSupplierSearchQuery.value })

  lastSupplierSearchQuery.value = ''
  supplierSearchQuery.value = ''

  if (result?.id) {
    const newId = parseInt(result.id)

    await nextTick()

    const newSupplier = suppliersStore.suppliersData.suppliers.find(s => s.id === newId)
    if (newSupplier) {
      if (!productSalonerSuppliers.value.some(s => s.id === newSupplier.id)) {
        productSalonerSuppliers.value.push({
          id: newSupplier.id,
          name: newSupplier.name,
          canBeUnassigned: newSupplier.canBeUnassigned,
        })
      }
      if (!editForm.value.salonerSuppliers.some(s => s.id === newSupplier.id)) {
        editForm.value.salonerSuppliers = [
          ...editForm.value.salonerSuppliers,
          { id: newSupplier.id, name: newSupplier.name },
        ]
      }
    }
  }
}

const handleSuppliersChange = async (newValue) => {
  const incoming = newValue || []

  const addNewSelected = incoming.some(s => s.id === '__add_new__')
  const filtered = incoming.filter(s => s.id !== '__add_new__')

  const nonRemovable = suppliersStore.suppliersData.suppliers.filter(s => !s.canBeUnassigned)
  const previouslySelectedNonRemovable = editForm.value.salonerSuppliers?.filter(selectedSupplier =>
    nonRemovable.some(nonRemovableSupplier => nonRemovableSupplier.id === selectedSupplier.id),
  )

  const result = [...filtered]
  for (const supplier of previouslySelectedNonRemovable) {
    if (!result.some(s => s.id === supplier.id)) {
      result.push(supplier)
    }
  }

  editForm.value.salonerSuppliers = result

  supplierSearchQuery.value = ""

  if (addNewSelected) {
    await handleSupplierAdd()
  }

  lastSupplierSearchQuery.value = ""
}

watch(
  () => editForm.value.category,
  value => {
    const normalized = normalizeInputValue(value)

    if (normalized !== categorySearch.value) {
      categorySearch.value = normalized
    }
  },
  { immediate: true },
)

watch(
  () => editForm.value.producer,
  value => {
    const normalized = normalizeInputValue(value)

    if (normalized !== producerSearch.value) {
      producerSearch.value = normalized
    }
  },
  { immediate: true },
)

const isDefaultUnit = computed(() => editForm.value.unit === transactionStore.DEFAULT_UNIT)

watch(
  () => editForm.value.unit,
  value => {
    if (value === transactionStore.DEFAULT_UNIT) {
      editForm.value.package = 1
    }
  },
)

const saveProduct = async () => {
  const validationResult = await editFormRef.value.validate()
  if (validationResult?.valid) {
    const response = await store.saveProduct({ ...editForm.value })
    if (response?.success) {
      showSuccessToast(response.message || t('save'))
      //console.log('response.data?.id', response.data?.id)
      const addedProduct = {
        ...editForm.value,
        id: response.data?.id,
        availableUnits: editForm.value.unit !== transactionStore.DEFAULT_UNIT ? [transactionStore.DEFAULT_UNIT, editForm.value.unit] : [editForm.value.unit],
        quantity: 0,
        previousPrice: product.value?.price, // Price before this edit - lets callers detect a real price change
      }
      // Notify other views (e.g. stocktaking) that this product's master data changed,
      // so they can refresh name / last buy price / units on their own lists.
      emitter.emit('storage-product-saved', { ...addedProduct })

      modalStore.closeModal(THIS_MODAL_NAME, addedProduct)
      //await store.fetchProducts({ page: store.meta.page, perPage: store.meta.perPage, force: true })
      // Load filters and products in parallel
      await Promise.all([
        store.fetchFilters(),
        store.fetchProducts({ page: store.meta.page, perPage: store.meta.perPage, force: true }),
      ])
    }
  } else {
    showWarningToast(t('form_has_errors'))
  }



}

/**
 * Handle delete product (placeholder)
 *
 */
const handleDelete = async () => {
  const confirmed = await modalStore.openConfirmationModal(
    t('storage.product_list.delete_this_confirmation'),
    t('warning_this_cannot_be_undone'),
  )
  if (!confirmed) return

  store.isSavingProduct = true

  const result = await store.deleteProduct(editForm.value.id)

  if (result.success) {
    showSuccessToast(result.message || t('storage.product_list.product_deleted'))
    modalStore.closeModal(THIS_MODAL_NAME)
    await store.fetchProducts({
      page: store.meta.page,
      perPage: store.meta.perPage,
      force: true,
    })
  } else {

  }

  store.isSavingProduct = false
}

// ========================= Clipboard (copy / paste / duplicate) =========================

// localStorage so copy in one tab can be pasted in another; entry expires after CLIPBOARD_TTL_MS.
const clipboard = useStorage(CLIPBOARD_KEY, null, localStorage, { serializer: StorageSerializers.object })

// ponytail: TTL evaluated on render and re-checked on click, no timer. A tab left open past
// expiry may show Paste enabled for a moment; handlePaste refuses it anyway.
const canPaste = computed(() => !!clipboard.value?.ts && Date.now() - clipboard.value.ts < CLIPBOARD_TTL_MS)

const handleCopy = () => {
  clipboard.value = { ts: Date.now(), form: { ...editForm.value, id: null, imageUrl: '' } }
  showSuccessToast(t('storage.product_list.copied'))
}

const handlePaste = () => {
  if (!canPaste.value) return

  // Keep the id of the product being edited - paste fills the fields, it never repoints the record.
  editForm.value = { ...clipboard.value.form, id: editForm.value.id }
  productSalonerSuppliers.value = editForm.value.salonerSuppliers ?? []
  showSuccessToast(t('storage.product_list.pasted'))
}

const handleDuplicate = () => {
  editForm.value.id = null
  editForm.value.name = t('storage.product_list.duplicate_prefix') + editForm.value.name

  // Identifiers and the photo belong to the original product, not to its copy.
  editForm.value.ean = ''
  editForm.value.internalCode = ''
  editForm.value.imageUrl = ''

  showSuccessToast(t('storage.product_list.duplicated'))
}

/**
 * Get product image URL or placeholder
 *
 * @param {Object} productTmp - Product object
 * @returns {string} Image URL
 */
const getProductImage = (productTmp) => {
  return productTmp.imageUrl || productPlaceholder
}
</script>

<template>
  <VDialog
    v-model="modalStore.modals.editStorageProduct"
    max-width="800px"
    :fullscreen="!smAndUp"
    persistent
    scrollable
  >
    <VCard :loading="store.isSavingProduct">
      <template #title>
        <div class="d-flex justify-space-between">
          <span>{{ $t(editForm.id ? 'storage.product_list.edit' : 'storage.product_list.add_product') }}</span>
          <VIcon
            icon="ri-close-line"
            :disabled="store.isSavingProduct"
            @click="modalStore.closeModal(THIS_MODAL_NAME)"
          />
        </div>
      </template>

      <VSkeletonLoader
        v-if="isLoading"
        class="w-100"
        :type="[ 'heading', 'heading', 'heading', 'heading', 'heading', 'heading', 'heading', 'paragraph', 'button']"
      />

      <VCardText v-else>
        <!-- Skeleton while loading -->
        <template v-if="store.isLoadingProduct">
          <VSkeletonLoader type="list-item-two-line@5,paragraph,button" />
        </template>

        <!-- Edit form -->
        <template v-else-if="product">
          <VForm
            ref="editFormRef"
            @submit.prevent
          >
            <VRow>
              <!-- Row 1: Nazwa produktu | Rodzaj produktu -->
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="editForm.name"
                  :label="$t('storage.product_list.product_name')"
                  density="compact"
                  color="secondary"
                  :rules="[validationRules.required]"
                  :disabled="store.isSavingProduct"
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VSelect
                  v-model="editForm.type"
                  :label="$t('storage.product_list.product_type')"
                  :items="typeOptions"
                  density="compact"
                  color="secondary"
                  :disabled="store.isSavingProduct"
                />
              </VCol>

              <!-- Row 2: Kod kreskowy (EAN) | Kod wewnętrzny -->
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="editForm.ean"
                  :label="$t('storage.product_list.barcode_ean')"
                  density="compact"
                  color="secondary"
                  :disabled="store.isSavingProduct"
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="editForm.internalCode"
                  :label="$t('storage.import.internal_code')"
                  density="compact"
                  color="secondary"
                  :disabled="store.isSavingProduct"
                />
              </VCol>

              <!-- Row 2.5: Dostawcy -->
              <VCol cols="12">
                <div class="d-flex align-center">
                  <VAutocomplete
                    v-model:search="supplierSearchQuery"
                    :model-value="editForm.salonerSuppliers"
                    :label="$t('storage.Suppliers')"
                    :items="supplierItems"
                    item-title="name"
                    item-value="id"
                    multiple
                    chips
                    closable-chips
                    return-object
                    no-filter
                    hide-no-data
                    auto-select-first
                    density="compact"
                    color="secondary"
                    :disabled="store.isSavingProduct"
                    @update:model-value="handleSuppliersChange"
                    @update:search="(val) => { if (val) lastSupplierSearchQuery = val }"
                  >
                    <template #chip="{ item, props: chipProps }">
                      <VChip
                        v-bind="chipProps"
                        :closable="!Boolean(suppliersStore.suppliersData.suppliers.find(s => s.id === item.value && !s.canBeUnassigned))"
                      />
                    </template>
                    <template #item="{ item, props: itemProps }">
                      <VListItem v-bind="itemProps">
                        <template
                          v-if="item.raw.id === '__add_new__'"
                          #prepend
                        />
                      </VListItem>
                    </template>
                  </VAutocomplete>
                  <VBtn
                    variant="flat"
                    color="primary"
                    class="ml-1"
                    :size="mobile ? 'small' : 'default'"
                    :disabled="store.isSavingProduct"
                    @click="handleSupplierAdd"
                  >
                    <VIcon
                      icon="ri-add-large-line"
                      :class="mobile ? '' : 'mr-1'"
                    />
                    <VTooltip
                      v-if="!mobile"
                      activator="parent"
                    >
                      {{ $t('storage.transactions.add_new_supplier') }}
                    </VTooltip>
                  </VBtn>
                </div>
              </VCol>

              <!-- Row 3: Producent | Kategoria -->
              <VCol
                cols="12"
                sm="6"
              >
                <VAutocomplete
                  v-model="editForm.producer"
                  v-model:search="producerSearch"
                  :label="$t('storage.import.producer')"
                  :items="producerOptions"
                  item-title="title"
                  item-value="value"
                  :auto-select-first="shouldOfferCustomProducer"
                  clearable
                  hide-no-data
                  density="compact"
                  color="secondary"
                  :disabled="store.isSavingProduct"
                  @keydown.enter="commitProducerSearch"
                  @blur="commitProducerSearch"
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VAutocomplete
                  v-model="editForm.category"
                  v-model:search="categorySearch"
                  :label="$t('storage.import.category')"
                  :items="categoryOptions"
                  item-title="title"
                  item-value="value"
                  :auto-select-first="shouldOfferCustomCategory"
                  clearable
                  hide-no-data
                  density="compact"
                  color="secondary"
                  :disabled="store.isSavingProduct"
                  @keydown.enter="commitCategorySearch"
                  @blur="commitCategorySearch"
                />
              </VCol>

              <!-- Row 4: Opakowanie | Jednostka zużycia | Minimalny stan -->
              <VCol
                cols="12"
                sm="4"
              >
                <VSelect
                  v-model="editForm.unit"
                  :label="$t('storage.product_list.usage_unit')"
                  :items="unitOptions"
                  density="compact"
                  color="secondary"
                  :disabled="store.isSavingProduct"
                />
              </VCol>
              <VCol
                v-if="!isDefaultUnit"
                cols="12"
                sm="4"
              >
                <VNumberInput
                  v-model="editForm.package"
                  :label="$t('storage.import.package')"
                  class="number-input"
                  variant="outlined"
                  density="compact"
                  color="secondary"
                  :min="0"
                  hide-spin-buttons
                  control-variant="hidden"
                  hide-details="auto"
                  :suffix="editForm.unit"
                  :rules="validationRules.fullNumberRules"
                  :disabled="isDefaultUnit || store.isSavingProduct"
                />
              </VCol>
              <VCol
                cols="12"
                :sm="isDefaultUnit ? 8 : 4"
              >
                <VNumberInput
                  v-model="editForm.minimal"
                  :label="$t('storage.product_list.minimal_stock')"
                  class="number-input"
                  variant="outlined"
                  density="compact"
                  color="secondary"
                  :min="0"
                  hide-spin-buttons
                  hide-details
                  control-variant="hidden"
                  :suffix="t('storage.product_list.pc')"
                  :disabled="store.isSavingProduct"
                />
              </VCol>

              <!-- Row 5: Cena sprzedaży netto | Cena sprzedaży brutto -->

              <VCol
                v-if="editForm.type !== 2"
                cols="12"
                sm="6"
              >
                <VNumberInput
                  v-model="editForm.priceNet"
                  :label="$t('storage.product_list.suggested_sell_price_net')"
                  class="number-input"
                  variant="outlined"
                  density="compact"
                  color="secondary"
                  :min="0"
                  :precision="2"
                  :min-fraction-digits="0"
                  hide-spin-buttons
                  hide-details
                  control-variant="hidden"
                  suffix="zł"
                  :disabled="store.isSavingProduct"
                  @update:model-value="handleSellPriceNetChange"
                />
              </VCol>
              <VCol
                v-if="editForm.type !== 2"
                cols="12"
                sm="6"
              >
                <VNumberInput
                  v-model="editForm.price"
                  :label="$t('storage.product_list.suggested_sell_price_gross')"
                  class="number-input"
                  variant="outlined"
                  density="compact"
                  color="secondary"
                  :min="0"
                  :precision="2"
                  :min-fraction-digits="0"
                  hide-spin-buttons
                  hide-details
                  control-variant="hidden"
                  suffix="zł"
                  :disabled="store.isSavingProduct"
                  @update:model-value="handleSellPriceGrossChange"
                />
              </VCol>

              <!-- Row 6: (offset) | Cena zakupu netto | Cena zakupu brutto -->
              <VCol cols="12">
                <div class="d-flex align-center flex-wrap ga-6">
                  <VSelect
                    v-model="editForm.tax"
                    style="min-width: 95px;"
                    class="flex-grow-1"
                    label="VAT"
                    :items="taxOptions"
                    density="compact"
                    color="secondary"
                    :disabled="store.isSavingProduct"
                    @update:model-value="handleSellTaxChange"
                  />
                  <VNumberInput
                    v-model="editForm.buyPriceNet"
                    :label="$t('storage.product_list.last_buy_price_net')"
                    style="min-width: 260px;"
                    class="number-input flex-grow-1"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    :min="0"
                    :precision="2"
                    :min-fraction-digits="0"
                    hide-spin-buttons
                    hide-details
                    control-variant="hidden"
                    suffix="zł"
                    :disabled="store.isSavingProduct"
                    @update:model-value="handleBuyPriceNetChange"
                  />
                  <VNumberInput
                    v-model="editForm.buyPrice"
                    :label="$t('storage.product_list.last_buy_price_gross')"
                    style="min-width: 260px;"
                    class="number-input flex-grow-1"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    :min="0"
                    :precision="2"
                    :min-fraction-digits="0"
                    hide-spin-buttons
                    hide-details
                    control-variant="hidden"
                    suffix="zł"
                    :disabled="store.isSavingProduct"
                    @update:model-value="handleBuyPriceGrossChange"
                  />
                </div>
              </VCol>



              <!-- Row 7: Prowizja -->
              <VCol
                v-if="editForm.type !== 2"
                cols="12"
              >
                <div class="d-flex align-center flex-wrap ga-4">
                  <div class="d-flex align-center ga-4">
                    <VCheckbox
                      :model-value="isCommissionEnabled"
                      :label="$t('storage.product_list.set_commission')"
                      style="min-width: 140px;"
                      density="compact"
                      color="primary"
                      class="my-2"
                      hide-details
                      :disabled="store.isSavingProduct"
                      @update:model-value="toggleCommission"
                    />
                    <VTooltip
                      :open-on-hover="!mobile"
                      :open-on-click="mobile"
                    >
                      <template #activator="{ props }">
                        <VIcon
                          v-bind="props"
                          icon="ri-question-line"
                          color="info"
                        />
                      </template>
                      <span>{{ $t('storage.product_list.commission_explanation') }}</span>
                    </VTooltip>
                  </div>
                  <VNumberInput
                    v-if="isCommissionEnabled"
                    v-model="editForm.commission"
                    :label="$t('storage.product_list.employee_commission')"
                    class="number-input"
                    style="min-width: 200px;"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    :min="0"
                    :max="100"
                    :precision="2"
                    :min-fraction-digits="0"
                    hide-spin-buttons
                    hide-details
                    control-variant="hidden"
                    suffix="%"
                    :disabled="!isCommissionEnabled || store.isSavingProduct"
                  />
                </div>
              </VCol>

              <!-- Row 8: Obrazek Opis -->
              <VCol
                v-if="editForm.imageUrl"
                cols="12"
                sm="4"
              >
                <VImg
                  :src="getProductImage(editForm)"
                  width="100%"
                  class="product-image bg-white rounded"
                >
                  <template #placeholder>
                    <VRow
                      align="center"
                      justify="center"
                      class="fill-height"
                    >
                      <VProgressCircular indeterminate />
                    </VRow>
                  </template>
                </VImg>
              </VCol>
              <VCol
                cols="12"
                :sm="editForm.imageUrl ? 8 : 12"
              >
                <VTextarea
                  v-model="editForm.description"
                  :label="$t('storage.product_list.description')"
                  color="secondary"
                  :rows="mobile ? 3 : 9"
                  style="min-height: 200px;"
                  :disabled="store.isSavingProduct"
                />
              </VCol>
            </VRow>
          </VForm>
        </template>
      </VCardText>

      <VCardActions v-if="product && !store.isLoadingProduct">
        <div class="d-flex justify-space-between flex-fill pa-2">
          <div class="d-flex ga-2">
            <VBtn
              variant="outlined"
              color="secondary"
              :disabled="store.isSavingProduct"
              @click="modalStore.closeModal(THIS_MODAL_NAME)"
            >
              {{ $t('cancel') }}
            </VBtn>

            <VBtn
              v-if="editForm.id"
              variant="text"
              color="secondary"
              prepend-icon="ri-delete-bin-6-line"
              :disabled="store.isSavingProduct"
              @click="handleDelete"
            >
              {{ $t('delete') }}
            </VBtn>

            <VBtn
              variant="text"
              color="secondary"
              size="small"
              :disabled="store.isSavingProduct"
            >
              <VIcon icon="ri-more-2-fill" />
              <VMenu activator="parent">
                <VList density="compact">
                  <VListItem
                    v-if="editForm.id"
                    prepend-icon="ri-file-add-line"
                    :title="$t('duplicate')"
                    @click="handleDuplicate"
                  />
                  <VListItem
                    prepend-icon="ri-file-copy-2-line"
                    :title="$t('copy')"
                    @click="handleCopy"
                  />
                  <VListItem
                    prepend-icon="ri-clipboard-line"
                    :title="$t('paste')"
                    :disabled="!canPaste"
                    @click="handlePaste"
                  />
                </VList>
              </VMenu>
            </VBtn>
          </div>

          <VBtn
            v-if="!isLoading"
            color="green"
            variant="flat"
            :disabled="store.isSavingProduct"
            @click="saveProduct"
          >
            {{ $t('save') }}
          </VBtn>
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
